import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../../firebaseConfig';
import { makeReplay } from './replayClock';
import { apiPost, isBackendConfigured } from '../../utils/backendApi';
import { useEntityList, updateEntity, deleteEntity } from '../../data/entities';
import { useAuth } from '../../AuthContext';

// Realtime feeds for one app's room, all Firestore `onSnapshot`:
//
//   useAppLive      orgs/{o}/apps/{a}/live/now — the live wire the host
//                   agent streams while we tell the plane we are watching
//                   (POST /live/watch every 30 s). New rows go on `feed`,
//                   a queue the scene drains each frame, each scheduled at
//                   its real offset inside the batch so a burst reads as a
//                   burst and a trickle as a trickle.
//   useRoomPresence orgs/{o}/apps/{a}/presence — who else is in the room
//                   (our own pose written every 2 s, rows > 60 s old ignored).
//   useAppDeploys   orgs/{o}/apps/{a}/deploys — the deploy in flight, for
//                   the construction animation.

export function useAppLive({ orgId, appId, enabled = true, fixture = null }) {
  const [live, setLive] = useState(null);
  const feed = useMemo(() => ({ queue: [], lastT: 0, seen: 0 }), []);
  useEffect(() => {
    if (!enabled || !orgId || !appId || fixture) return undefined;
    const unsub = db.doc(`orgs/${orgId}/apps/${appId}/live/now`).onSnapshot((snap) => {
      const d = snap.exists ? snap.data() : null;
      setLive(d);
      if (!d?.ev?.length) return;
      // Only rows newer than the newest we have scheduled; the first
      // snapshot seeds `lastT` without replaying history into one burst.
      if (!feed.seen) { feed.seen = 1; feed.lastT = d.ev[d.ev.length - 1].t; return; }
      const fresh = d.ev.filter((e) => e.t > feed.lastT);
      if (!fresh.length) return;
      feed.lastT = fresh[fresh.length - 1].t;
      const t0 = fresh[0].t; const span = Math.min(2000, Math.max(0, feed.lastT - t0));
      const now = performance.now();
      for (const e of fresh) feed.queue.push({ ...e, due: now + (span ? ((e.t - t0) / span) * 1800 : 0) });
      if (feed.queue.length > 120) feed.queue.splice(0, feed.queue.length - 120);
    }, () => setLive(null));
    return () => unsub();
  }, [orgId, appId, enabled, fixture, feed]);
  // Tell the plane we are looking (it streams for apps.live.watchMs).
  useEffect(() => {
    if (!enabled || !orgId || !appId || fixture || !isBackendConfigured()) return undefined;
    let alive = true;
    const ping = () => { if (alive && document.visibilityState === 'visible') apiPost(`/apps/${orgId}/${appId}/live/watch`).catch(() => {}); };
    ping();
    const t = setInterval(ping, 30000);
    document.addEventListener('visibilitychange', ping);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', ping); };
  }, [orgId, appId, enabled, fixture]);
  return { live: fixture || live, feed };
}

const PRESENCE_MS = 2000;
export function useRoomPresence({ orgId, appId, ctl, enabled = true }) {
  const { user } = useAuth();
  const scope = useMemo(() => ({ org: orgId, appInstance: appId }), [orgId, appId]);
  const { docs } = useEntityList('appPresence', scope, { orderBy: null, enabled: enabled && !!orgId && !!appId });
  const last = useRef('');
  useEffect(() => {
    if (!enabled || !orgId || !appId || !user?.uid || !ctl) return undefined;
    const name = user.displayName || (user.email ? user.email.split('@')[0] : 'someone');
    const tick = () => {
      const pose = ctl.pose();
      if (!pose) return;
      const key = pose.position.map((v) => v.toFixed(1)).join(',');
      const idle = key === last.current;
      last.current = key;
      // Pose when it changed, heartbeat regardless (every 5th tick).
      if (idle && Math.floor(performance.now() / PRESENCE_MS) % 5 !== 0) return;
      updateEntity('appPresence', scope, user.uid, { uid: user.uid, name, pose: { position: pose.position.map((v) => Math.round(v * 100) / 100), target: pose.target.map((v) => Math.round(v * 100) / 100) }, at: Date.now() }).catch(() => {});
    };
    tick();
    const t = setInterval(tick, PRESENCE_MS);
    return () => { clearInterval(t); deleteEntity('appPresence', scope, user.uid).catch(() => {}); };
  }, [orgId, appId, enabled, user?.uid, user?.displayName, user?.email, ctl, scope]);
  const [, bump] = useState(0);
  useEffect(() => { const t = setInterval(() => bump((x) => x + 1), 10000); return () => clearInterval(t); }, []);
  const now = Date.now();
  return useMemo(() => (docs || []).filter((d) => d.uid !== user?.uid && d.pose?.position && now - (d.at || 0) < 60000), [docs, user?.uid, now]);
}

// The stage's live layer in one object (TopologyScene `wire`): the wire +
// feed from useAppCommand, the deploy in flight, who else is here (rooms
// only — needs the camera controller for the pose), the replay clock, and
// `onJump` for clicking a viewer. Memoized so the scene's provider only
// re-renders when something in it changed.
export function useAppWire({ orgId, appId, live, feed, ctl = null, enabled = true, fixture = false, replay = null }) {
  const deploy = useAppDeploys({ orgId, appId, enabled: enabled && !fixture });
  const viewers = useRoomPresence({ orgId, appId, ctl, enabled: enabled && !fixture && !!ctl });
  const onJump = useCallback((pose) => ctl?.flyTo(pose, 900), [ctl]);
  const [, setN] = useState(0);
  const rp = useMemo(() => (replay === false ? null : makeReplay(() => setN((x) => x + 1))), [replay]);
  return useMemo(() => ({ live, feed, deploy, viewers, onJump, replay: rp, narrate: true }), [live, feed, deploy, viewers, onJump, rp]);
}

export function useAppDeploys({ orgId, appId, enabled = true }) {
  const scope = useMemo(() => ({ org: orgId, appInstance: appId }), [orgId, appId]);
  const { docs } = useEntityList('appDeploy', scope, { orderBy: 'createdAt', dir: 'desc', enabled: enabled && !!orgId && !!appId });
  return useMemo(() => {
    const list = docs || [];
    const running = list.find((d) => d.status === 'running' || d.status === 'queued') || null;
    const last = list[0] || null;
    return { running, last, recentDoneAt: last && last.status === 'done' && last.finishedAt && Date.now() - last.finishedAt < 90e3 ? last.finishedAt : null };
  }, [docs]);
}
